import { inject, Injectable } from "@angular/core";
import { from, lastValueFrom, map, switchMap, tap } from "rxjs";
import { AccountBackend } from "src/app/backend/api/account.backend";
import { UserBackend } from "src/app/backend/api/user.backend";
import { ErrorCodes } from "src/app/modules/maintenance/error-codes";
import { AuthService } from "src/app/services/auth.service";
import { LocalStorageService } from "src/app/services/local-storage.service";
import { ProvideMemberAuthComponent } from "src/app/services/member-auth-provider/components/provide-member-auth.component";
import {
  MemberAuthProviderResult,
  MemberAuthProviderService
} from "src/app/services/member-auth-provider/member-auth-provider.service";
import { MemberWithTenant } from "src/app/services/member-auth-provider/members-with-tenant";
import { PopupService } from "src/app/services/popup/popup.service";
import { ProductService } from "src/app/services/product.service";
import { reportMessageWithExtraScope } from "src/app/support/error-reporting";

@Injectable()
export class MemberAuthProviderDefaultService extends MemberAuthProviderService {
  readonly #accountBackend = inject(AccountBackend);
  readonly #authService = inject(AuthService);
  readonly #userBackend = inject(UserBackend);
  readonly #productService = inject(ProductService);
  readonly #localStorageService = inject(LocalStorageService);
  readonly #popupService = inject(PopupService);

  async provide({
    accountNickName
  }: {
    accountNickName?: string;
  }): Promise<MemberAuthProviderResult> {
    const result: MemberAuthProviderResult = {};

    const product = this.#productService
      .getCurrentProduct()
      .toString()
      .toLowerCase();

    const { membersWithTenant, membersWithTenantFilteredByProduct } =
      await lastValueFrom(
        this.#accountBackend.listBySlugPublic({ slug: accountNickName! }).pipe(
          tap(async (accounts) => {
            if (!accounts || accounts.length === 0) {
              const authHeader =
                await this.#authService.getAuthorizationBearerToken();

              reportMessageWithExtraScope("ACCOUNTS_EMPTY", {
                authHeader: authHeader
              });
            }
          }),
          map((accounts) => accounts[0]),
          switchMap((account) => {
            return this.#userBackend.loadMembers(account!.id).pipe(
              map((members) => {
                return members.filter(
                  (member) => member.account === account?.id
                );
              }),
              switchMap((members) => {
                return from(
                  Promise.all(
                    members.map((member) => {
                      return lastValueFrom(
                        this.#userBackend.loadTenant(member.tenant).pipe(
                          map((tenant) => {
                            return {
                              member,
                              tenant
                            };
                          })
                        )
                      );
                    })
                  )
                );
              }),
              map((membersWithTenant) => {
                return {
                  membersWithTenant,
                  membersWithTenantFilteredByProduct: membersWithTenant.filter(
                    (x) => x.tenant.type === product
                  )
                };
              })
            );
          })
        )
      );

    const enabledMembersWithTenantFilterdByProduct =
      membersWithTenantFilteredByProduct.filter((x) => !x.tenant.disabled);

    result.membersWithTenant = membersWithTenant;
    result.availableMembersAndTenants = membersWithTenantFilteredByProduct;

    const preSelectedMemberWithTenant = this.#pickMemberWithTenantFromStorage(
      enabledMembersWithTenantFilterdByProduct
    );

    switch (true) {
      case membersWithTenantFilteredByProduct.length === 0: {
        result.error = ErrorCodes.ERR_016;
        break;
      }
      case enabledMembersWithTenantFilterdByProduct.length === 0: {
        if (membersWithTenantFilteredByProduct.length === 1) {
          result.error = ErrorCodes.ERR_009;
        } else {
          result.error = ErrorCodes.ERR_010;
        }

        break;
      }
      case membersWithTenantFilteredByProduct.length === 1: {
        const { member, tenant } = membersWithTenantFilteredByProduct[0]!;

        result.memberAuth = member;
        result.tenant = tenant;
        break;
      }
      case !!preSelectedMemberWithTenant: {
        result.memberAuth = preSelectedMemberWithTenant!.member;
        result.tenant = preSelectedMemberWithTenant!.tenant;

        break;
      }
      default: {
        const pickedMember = await this.#popupService.show<MemberWithTenant>(
          ProvideMemberAuthComponent,
          membersWithTenantFilteredByProduct,
          {
            dialog: true,
            hideButtonClose: true,
            attachToBody: true,
            height: 138 * membersWithTenantFilteredByProduct.length
          }
        );

        if (pickedMember) {
          result.tenant = pickedMember!.tenant;
          result.memberAuth = pickedMember!.member;
        } else {
          result.error = ErrorCodes.ERR_016;
        }
        break;
      }
    }

    return result;
  }

  #pickMemberWithTenantFromStorage(
    enabledMembersWithTenantFilterdByProduct: MemberWithTenant[]
  ): MemberWithTenant | undefined {
    const storedMemberId = this.#localStorageService.getItem("member-id");
    const storedTenantId = this.#localStorageService.getItem("tenant-id");

    return enabledMembersWithTenantFilterdByProduct.find(
      (x: MemberWithTenant) =>
        x.member.id === storedMemberId && x.tenant.id === storedTenantId
    );
  }
}
